import React from 'react';

const getAqiDetails = (val) => {
  if (val === null || val === undefined) return { text: "Unknown", color: "#abb2bf" };
  if (val <= 50) return { text: "Good", color: "#00e400" };
  if (val <= 100) return { text: "Moderate", color: "#ffff00" };
  if (val <= 150) return { text: "Unhealthy (S)", color: "#ff7e00" };
  if (val <= 200) return { text: "Unhealthy", color: "#ff0000" };
  if (val <= 300) return { text: "Very Unhealthy", color: "#8f3f97" };
  return { text: "Hazardous", color: "#7e0023" };
};

function CurrentConditions({ weatherData, locationName, coordinates, aqi, aqiLoading, detailedWeather, hourlyData, visibility, loading, gridpointData }) {
  if (!weatherData) {
    return (
      <div className="current-conditions-container">
        <div className="no-location-message">
          <div className="no-location-icon">📍</div>
          <h2>Search for a Location</h2>
          <p>Enter a city name or click anywhere on the map to view weather conditions</p>
        </div>
      </div>
    );
  }

  const aqiInfo = getAqiDetails(aqi);
  const currentConditions = weatherData[0]; // First period is current/most recent

  const radarSettings = {
    agenda: {
      id: "local",
      center: [coordinates.lon, coordinates.lat],
      location: null,
      zoom: 8,
      filter: "WSR-88D",
      layer: "sr_bref",
      station: null
    },
    animating: true,
    base: "standard",
    artcc: false,
    county: false,
    cwa: false,
    rfc: false,
    state: false,
    menu: true,
    shortFusedOnly: true,
    opacity: { alerts: 0.8, local: 0.6, localStations: 0.8, national: 0.6 }
  };
  const radarUrl = `https://radar.weather.gov/?${new URLSearchParams({
    settings: `v1_${btoa(JSON.stringify(radarSettings))}`
  }).toString()}`;
  
  // Get Open-Meteo current weather data
  const current = detailedWeather?.current || null;
  
  // Convert degrees to compass direction
  const degreesToCompass = (degrees) => {
    if (degrees === null || degrees === undefined) return 'N';
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };
  
  // Get wind data from Open-Meteo
  const getWindData = () => {
    if (current && current.wind_speed_10m !== undefined && current.wind_direction_10m !== undefined) {
      // Open-Meteo gives wind speed in mph (we set the unit), wind direction in degrees
      const speedMph = Math.round(current.wind_speed_10m);
      const gustMph = current.wind_gusts_10m ? Math.round(current.wind_gusts_10m) : 0;
      const windDirection = current.wind_direction_10m;
      
      return {
        direction: degreesToCompass(windDirection),
        directionDegrees: windDirection,
        speed: speedMph,
        gusts: gustMph
      };
    }
    
    // Fallback to forecast data if no current data available
    const direction = currentConditions.windDirection || 'N';
    const speedMatch = currentConditions.windSpeed?.match(/\d+/);
    const speed = speedMatch ? parseInt(speedMatch[0]) : 0;
    
    const compassToDegrees = (dir) => {
      const directions = {
        'N': 0, 'NNE': 22.5, 'NE': 45, 'ENE': 67.5,
        'E': 90, 'ESE': 112.5, 'SE': 135, 'SSE': 157.5,
        'S': 180, 'SSW': 202.5, 'SW': 225, 'WSW': 247.5,
        'W': 270, 'WNW': 292.5, 'NW': 315, 'NNW': 337.5
      };
      return directions[dir] || 0;
    };
    
    return { 
      direction, 
      directionDegrees: compassToDegrees(direction), 
      speed, 
      gusts: 0
    };
  };
  
  const windData = getWindData();

  // Helper function to get UV level description
  const getUVLevel = (uv) => {
    if (!uv) return "Low";
    if (uv <= 2) return "Low";
    if (uv <= 5) return "Moderate";
    if (uv <= 7) return "High";
    if (uv <= 10) return "Very High";
    return "Extreme";
  };

  // Helper function to format time
  const formatTime = (datetime) => {
    if (!datetime) return "--";
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Helper function to convert hPa to inHg
  const hpaToInHg = (hpa) => {
    if (!hpa) return 0;
    return (hpa * 0.02953).toFixed(2);
  };

  // Calculate precipitation from Open-Meteo hourly data (past 24h) and NWS gridpoint (future 24h)
  const calculatePrecipitation = () => {
    if (!hourlyData || hourlyData.length === 0) {
      return { current: 0, next24h: 0, currentType: 'precipitation', futureType: 'precipitation' };
    }

    // Calculate accumulated precipitation over last 24h from Open-Meteo hourly data
    let recentPrecip = 0;
    let recentSnow = 0;
    let currentHasSnow = false;
    let currentHasRain = false;
    
    let futurePrecip = 0;
    let futureSnow = 0;
    let futureHasSnow = false;
    let futureHasRain = false;
    
    if (detailedWeather?.hourly?.precipitation && detailedWeather?.hourly?.time) {
      const hourlyTimes = detailedWeather.hourly.time;
      const hourlyPrecip = detailedWeather.hourly.precipitation;
      const hourlySnow = detailedWeather.hourly.snowfall || [];
      
      // Find the current time index (or most recent)
      const now = new Date();
      let currentIndex = -1;
      
      for (let i = 0; i < hourlyTimes.length; i++) {
        const hourTime = new Date(hourlyTimes[i]);
        if (hourTime <= now) {
          currentIndex = i;
        } else {
          break;
        }
      }
      
      if (currentIndex >= 0) {
        // Sum up last 24 hours of precipitation (including current hour)
        const startIndex = Math.max(0, currentIndex - 23);
        for (let i = startIndex; i <= currentIndex; i++) {
          const precip = hourlyPrecip[i] || 0;
          const snow = hourlySnow[i] || 0;
          recentPrecip += precip;
          recentSnow += snow;
          
          if (snow > 0) currentHasSnow = true;
          if (precip > 0 && snow === 0) currentHasRain = true;
        }
      }
    }
    
    // Calculate future precipitation from NWS gridpoint data (more accurate for US)
    if (gridpointData) {
      const now = new Date();
      const next24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      
      // Parse NWS gridpoint quantitativePrecipitation
      if (gridpointData.quantitativePrecipitation?.values) {
        gridpointData.quantitativePrecipitation.values.forEach(item => {
          const [startTime, duration] = item.validTime.split('/');
          const start = new Date(startTime);
          
          // Only include precipitation within next 24 hours
          if (start > now && start <= next24h && item.value !== null) {
            // Convert mm to inches (NWS gives mm)
            futurePrecip += item.value * 0.0393701;
            if (item.value > 0) futureHasRain = true;
          }
        });
      }
      
      // Parse NWS gridpoint snowfallAmount
      if (gridpointData.snowfallAmount?.values) {
        gridpointData.snowfallAmount.values.forEach(item => {
          const [startTime, duration] = item.validTime.split('/');
          const start = new Date(startTime);
          
          // Only include snowfall within next 24 hours
          if (start > now && start <= next24h && item.value !== null) {
            // Convert mm to inches (NWS gives mm)
            futureSnow += item.value * 0.0393701;
            if (item.value > 0) {
              futureHasSnow = true;
              futureHasRain = false; // Snow overrides rain classification
            }
          }
        });
      }
    }

    // Determine precipitation type
    const getPrecipType = (hasSnow, hasRain) => {
      if (hasSnow && hasRain) return 'mixed precip';
      if (hasSnow) return 'snowfall';
      if (hasRain) return 'rainfall';
      return 'precipitation';
    };

    return { 
      current: recentPrecip.toFixed(2), 
      currentSnow: recentSnow.toFixed(1),
      next24h: futurePrecip.toFixed(2),
      next24hSnow: futureSnow.toFixed(1),
      currentType: getPrecipType(currentHasSnow, currentHasRain),
      futureType: getPrecipType(futureHasSnow, futureHasRain)
    };
  };

  const precipitation = hourlyData ? calculatePrecipitation() : { 
    current: 0,
    currentSnow: 0,
    next24h: 0,
    next24hSnow: 0,
    currentType: 'precipitation', 
    futureType: 'precipitation' 
  };

  return (
    <div className="current-conditions-container">
      <div className="location-header">
        <span className="location-label">LOCATION</span>
        <h1 className="location-name">{locationName || "Detecting Location..."}</h1>
        <a className="radar-link" href={radarUrl} target="_blank" rel="noopener noreferrer">
          View detailed radar on weather.gov ↗
        </a>
      </div>

      {/* Loading Spinner */}
      {loading && weatherData && (
        <div className="conditions-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading current conditions...</p>
        </div>
      )}

      {/* Current Conditions Section - Apple Weather Style */}
      {currentConditions && !loading && (
        detailedWeather ? (
        <div className="current-conditions">
          {/* Main Temperature Display - Open-Meteo Current */}
          <div className="current-hero">
            <div className="hero-temp">
              {current && current.temperature_2m !== undefined ? 
                Math.round(current.temperature_2m) : 
                currentConditions.temperature}°
            </div>
            <div className="hero-desc">{currentConditions.shortForecast}</div>
            <div className="hero-range">
              {/* Get today's high/low from forecast periods */}
              {(() => {
                const today = weatherData.slice(0, 2); // Today/Tonight
                const temps = today.map(p => p.temperature);
                const high = Math.max(...temps);
                const low = Math.min(...temps);
                return `H: ${high}° L: ${low}°`;
              })()}
            </div>
          </div>

          {/* Weather Stats Grid - Apple Weather Style */}
          <div className="weather-grid">
            {/* UV Index */}
            <div className="weather-stat">
              <div className="stat-icon">☀️</div>
              <div className="stat-label">UV INDEX</div>
              <div className="stat-value">{detailedWeather.current.uv_index || 0}</div>
              <div className="stat-desc">{getUVLevel(detailedWeather.current.uv_index)}</div>
            </div>

            {/* Sunrise */}
            <div className="weather-stat">
              <div className="stat-icon">🌅</div>
              <div className="stat-label">SUNRISE</div>
              <div className="stat-value">{formatTime(detailedWeather.daily.sunrise[0])}</div>
              <div className="stat-desc">Sunset: {formatTime(detailedWeather.daily.sunset[0])}</div>
            </div>

            {/* Wind - Using NWS Observations */}
            <div className="weather-stat">
              <div className="stat-icon">💨</div>
              <div className="stat-label">WIND</div>
              <div className="stat-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span 
                  className="wind-arrow" 
                  style={{ 
                    transform: `rotate(${windData.directionDegrees}deg)`,
                    display: 'inline-block'
                  }}
                >
                  ↓
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', lineHeight: '1.2' }}>
                  <span style={{ fontSize: '1.5rem' }}>
                    {windData.direction} {windData.speed} mph
                  </span>
                  {windData.gusts > 0 && (
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)', fontWeight: '500' }}>
                      Gusts to {windData.gusts} mph
                    </span>
                  )}
                </div>
              </div>
              <div className="stat-desc">
                {windData.gusts > 0 ? 'Sustained & gusts' : 'Wind speed'}
              </div>
            </div>

            {/* Feels Like - Open-Meteo Data */}
            <div className="weather-stat">
              <div className="stat-icon">🌡️</div>
              <div className="stat-label">FEELS LIKE</div>
              <div className="stat-value">
                {current && current.apparent_temperature !== undefined ? 
                  `${Math.round(current.apparent_temperature)}°` : 
                  `${currentConditions.temperature}°`}
              </div>
              <div className="stat-desc">
                {(() => {
                  if (!current || current.apparent_temperature === undefined) return 'Same as actual';
                  const diff = Math.abs(current.apparent_temperature - current.temperature_2m);
                  if (diff < 3) return 'Same as actual';
                  if (current.apparent_temperature < current.temperature_2m) return 'Wind chill';
                  return 'Heat index';
                })()}
              </div>
            </div>

            {/* Humidity - Open-Meteo Data */}
            <div className="weather-stat">
              <div className="stat-icon">💧</div>
              <div className="stat-label">HUMIDITY</div>
              <div className="stat-value">
                {current && current.relative_humidity_2m !== undefined ? 
                  Math.round(current.relative_humidity_2m) : 
                  currentConditions.relativeHumidity?.value || '--'}%
              </div>
              <div className="stat-desc">
                {current && current.relative_humidity_2m > 65 ? 'High humidity' : 'Comfortable'}
              </div>
            </div>

            {/* Visibility - NWS Data */}
            <div className="weather-stat">
              <div className="stat-icon">👁️</div>
              <div className="stat-label">VISIBILITY</div>
              <div className="stat-value">
                {visibility !== null ? visibility : '10+'} mi
              </div>
              <div className="stat-desc">
                {visibility && parseFloat(visibility) > 10 ? 'Clear view' : 'Good visibility'}
              </div>
            </div>

            {/* Pressure - Open-Meteo Data */}
            <div className="weather-stat">
              <div className="stat-icon">📊</div>
              <div className="stat-label">PRESSURE</div>
              <div className="stat-value">
                {current && current.pressure_msl !== undefined ? 
                  hpaToInHg(current.pressure_msl) : 
                  '29.92'} inHg
              </div>
              <div className="stat-desc">Sea level pressure</div>
            </div>

            {/* Air Quality */}
            <div className="weather-stat">
              <div className="stat-icon">🍃</div>
              <div className="stat-label">AIR QUALITY</div>
              <div className="stat-value" style={{ color: aqiInfo.color }}>
                {aqiLoading ? '...' : (aqi || '--')}
              </div>
              <div className="stat-desc">{aqiLoading ? 'Loading AQI...' : aqiInfo.text}</div>
            </div>

            {/* Precipitation - Last 24 Hours */}
            <div className="weather-stat">
              <div className="stat-icon">{precipitation.currentType === 'snowfall' ? '❄️' : '🌧️'}</div>
              <div className="stat-label">PRECIP (LAST 24H)</div>
              <div className="stat-value" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div>
                  {precipitation.currentSnow && parseFloat(precipitation.currentSnow) > 0 ? (
                    <>
                      <span style={{ fontSize: '1.8rem' }}>{precipitation.currentSnow}"</span>
                      <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)', marginLeft: '6px' }}>snow</span>
                    </>
                  ) : (
                    <span>{precipitation.current} in</span>
                  )}
                </div>
                {precipitation.currentSnow && parseFloat(precipitation.currentSnow) > 0 && (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', fontWeight: '500' }}>
                    {precipitation.current}" liquid equiv.
                  </div>
                )}
              </div>
              <div className="stat-desc">
                {parseFloat(precipitation.current) === 0 ? 'No precipitation' : ''}
              </div>
            </div>

            {/* Precipitation - Next 24 Hours - Open-Meteo Forecast */}
            <div className="weather-stat">
              <div className="stat-icon">{precipitation.futureType === 'snowfall' ? '🌨️' : '☔'}</div>
              <div className="stat-label">PRECIP (NEXT 24H)</div>
              <div className="stat-value" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div>
                  {precipitation.next24hSnow && parseFloat(precipitation.next24hSnow) > 0 ? (
                    <>
                      <span style={{ fontSize: '1.8rem' }}>{precipitation.next24hSnow}"</span>
                      <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)', marginLeft: '6px' }}>snow</span>
                    </>
                  ) : (
                    <span>{precipitation.next24h} in</span>
                  )}
                </div>
                {precipitation.next24hSnow && parseFloat(precipitation.next24hSnow) > 0 && (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', fontWeight: '500' }}>
                    {precipitation.next24h}" liquid equiv.
                  </div>
                )}
              </div>
              <div className="stat-desc">
                {parseFloat(precipitation.next24h) === 0 ? 'None expected' : ''}
              </div>
            </div>
          </div>
        </div>
        ) : (
          <div className="current-conditions">
            <div className="current-hero">
              <div className="hero-temp">{currentConditions.temperature}°</div>
              <div className="hero-desc">{currentConditions.shortForecast}</div>
            </div>
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-dim)' }}>
              Loading detailed weather data...
            </div>
          </div>
        )
      )}
    </div>
  );
}

export default CurrentConditions;
